/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import React from 'react';
import type {
  MaritimeWarningItem,
  MaritimeWarningItemAttributes,
} from './types';

let unpublishedWarningId = 0;
export function createWarning(): MaritimeWarningItem {
  unpublishedWarningId += 1;
  return {
    assignedAreaIds: [],
    id: `unpublished-warning-${unpublishedWarningId}`,
  };
}

export function isPublishedWarning(warning: MaritimeWarningItem): boolean {
  return !warning.id.startsWith('unpublished-warning');
}

interface State {
  selectedAreaIds: string[];
  warnings: MaritimeWarningItem[];
  modified: boolean;
}

export const initialState: State = {
  selectedAreaIds: [],
  warnings: [createWarning()],
  modified: false,
};

// Possible actions on the reducer
interface Initialize {
  type: 'initialize';
  warnings: MaritimeWarningItem[];
}
interface ToggleSelectArea {
  type: 'toggleSelectArea';
  id: string;
}

interface SelectMultipleAreas {
  type: 'selectMultipleAreas';
  ids: string[];
}
interface NewWarning {
  type: 'newWarning';
}
interface AddSelectedAreasToWarning {
  type: 'addSelectedAreasToWarning';
  id: string;
}
interface RemoveWarning {
  type: 'removeWarning';
  id: string;
}
interface ChangeWarning {
  type: 'changeWarning';
  id: string;
  newValues: Partial<MaritimeWarningItemAttributes>;
}
interface RemoveArea {
  type: 'removeArea';
  areaId: string;
}
interface ClearWarnings {
  type: 'clearWarnings';
}

interface MoveWarning {
  type: 'moveWarning';
  oldIndex: number;
  newIndex: number;
}

interface MergeWarnings {
  type: 'mergeWarnings';
}

interface SubmitWarnings {
  type: 'submitWarnings';
}

type Action =
  | ToggleSelectArea
  | NewWarning
  | Initialize
  | AddSelectedAreasToWarning
  | ChangeWarning
  | RemoveWarning
  | RemoveArea
  | ClearWarnings
  | MoveWarning
  | SelectMultipleAreas
  | MergeWarnings
  | SubmitWarnings;

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'changeWarning': {
      return {
        ...state,
        warnings: state.warnings.map((warning) => {
          if (warning.id !== action.id) {
            return warning;
          }

          return {
            ...warning,
            ...action.newValues,
          };
        }),
        modified: true,
      };
    }
    case 'toggleSelectArea': {
      if (state.selectedAreaIds.includes(action.id)) {
        return {
          ...state,
          selectedAreaIds: state.selectedAreaIds.filter(
            (areaId) => areaId !== action.id,
          ),
        };
      }
      return {
        ...state,
        selectedAreaIds: [...state.selectedAreaIds, action.id],
      };
    }
    case 'selectMultipleAreas': {
      const newSelectedAreaIds = new Set([
        ...state.selectedAreaIds,
        ...action.ids,
      ]);

      if (newSelectedAreaIds.size === state.selectedAreaIds.length) {
        return state;
      }

      return {
        ...state,
        selectedAreaIds: Array.from(newSelectedAreaIds),
      };
    }
    case 'removeArea': {
      return {
        ...state,
        selectedAreaIds: state.selectedAreaIds.filter(
          (areaId) => areaId !== action.areaId,
        ),
        warnings: state.warnings.map((warning) => {
          const warningHasASelectedArea = warning.assignedAreaIds.some(
            (areaId) => areaId === action.areaId,
          );

          if (warningHasASelectedArea) {
            return {
              ...warning,
              assignedAreaIds: warning.assignedAreaIds.filter(
                (areaId) => areaId !== action.areaId,
              ),
            };
          }

          return warning;
        }),
        modified: true,
      };
    }
    case 'newWarning': {
      return {
        ...state,
        warnings: [...state.warnings, createWarning()],
      };
    }
    case 'addSelectedAreasToWarning': {
      const selectedAreaIdsSet = new Set(state.selectedAreaIds);

      return {
        ...state,
        warnings: state.warnings.map((warning) => {
          // remove the selected areas from other warnings
          if (warning.id !== action.id) {
            const warningHasASelectedArea = warning.assignedAreaIds.some(
              (areaId) => selectedAreaIdsSet.has(areaId),
            );

            // don't modify the warning if it does not have a selected area
            if (!warningHasASelectedArea) {
              return warning;
            }

            return {
              ...warning,
              assignedAreaIds: warning.assignedAreaIds.filter(
                (areaId) => !selectedAreaIdsSet.has(areaId),
              ),
            };
          }

          // add the area to the given warning

          const newAreaIds = new Set([
            ...warning.assignedAreaIds,
            ...state.selectedAreaIds,
          ]);

          return {
            ...warning,
            assignedAreaIds: Array.from(newAreaIds),
          };
        }),
        selectedAreaIds: [],
        modified: true,
      };
    }
    case 'removeWarning': {
      const newWarnings = state.warnings.filter(
        (warning) => warning.id !== action.id,
      );

      return {
        ...state,
        warnings: newWarnings.length > 0 ? newWarnings : initialState.warnings,
        modified: true,
      };
    }
    case 'clearWarnings': {
      return {
        ...initialState,
        modified: true,
      };
    }
    case 'moveWarning': {
      const oldWarning = state.warnings[action.oldIndex];

      const newWarnings = [...state.warnings];
      newWarnings.splice(action.oldIndex, 1);
      newWarnings.splice(action.newIndex, 0, oldWarning);

      return {
        ...state,
        warnings: newWarnings,
        modified: true,
      };
    }
    case 'mergeWarnings': {
      const clonedWarnings = new Set<number>();

      const mergedWarnings = state.warnings.reduce(
        (acc: MaritimeWarningItem[], warning) => {
          const relatedWarningIndex = acc.findLastIndex((otherWarning) => {
            return (
              warning.windDirectionFrom === otherWarning.windDirectionFrom &&
              warning.windForce === otherWarning.windForce &&
              warning.changeType === otherWarning.changeType &&
              warning.windDirectionTo === otherWarning.windDirectionTo
            );
          });

          if (relatedWarningIndex > -1) {
            const relatedWarning = acc[relatedWarningIndex];

            // clone the warning to make sure React knows that it is a new warning
            if (!clonedWarnings.has(relatedWarningIndex)) {
              clonedWarnings.add(relatedWarningIndex);

              const clonedRelatedWarning = {
                ...relatedWarning,
                assignedAreaIds: [
                  ...relatedWarning.assignedAreaIds,
                  ...warning.assignedAreaIds,
                ],
              };

              return acc.with(relatedWarningIndex, clonedRelatedWarning);
            }

            relatedWarning.assignedAreaIds = [
              ...relatedWarning.assignedAreaIds,
              ...warning.assignedAreaIds,
            ];

            return acc;
          }

          return [...acc, warning];
        },
        [],
      );

      return {
        ...state,
        warnings: mergedWarnings,
      };
    }
    case 'initialize': {
      if (action.warnings.length === 0) {
        return initialState;
      }
      return {
        ...state,
        warnings: action.warnings,
        selectedAreaIds: [],
      };
    }
    case 'submitWarnings': {
      return {
        ...state,
        modified: false,
      };
    }
    default: {
      return state;
    }
  }
}

export function useMaritimeWarningsFormState(): [
  State,
  React.Dispatch<Action>,
] {
  return React.useReducer(reducer, initialState);
}
